import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';
import { useAuth } from './AuthContext';
import {
  isPushSupported,
  getNotificationPermission,
  requestNotificationPermission,
  subscribeToPush,
  isSubscribedToPush,
  showLocalNotification
} from '../services/pushService';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const NotificationsContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications debe usarse dentro de NotificationsProvider');
  }
  return context;
};

export const NotificationsProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState(null);
  const [pushPermission, setPushPermission] = useState('default');
  const [pushSubscribed, setPushSubscribed] = useState(false);

  // Calcular no leídas
  const unreadCount = notifications.filter(n => !n.read).length;

  // Obtener notificaciones del servidor
  const fetchNotifications = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/notifications`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const notifs = data.notifications || data.response || data || [];
        setNotifications(Array.isArray(notifs) ? notifs : []);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Marcar una como leída
  const markAsRead = useCallback(async (notificationId) => {
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setNotifications(prev =>
          prev.map(n => n._id === notificationId ? { ...n, read: true } : n)
        );
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, [token]);

  // Marcar todas como leídas
  const markAllAsRead = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/notifications/read-all`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  }, [token]);

  // Eliminar notificación
  const deleteNotification = useCallback(async (notificationId) => {
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setNotifications(prev => prev.filter(n => n._id !== notificationId));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  }, [token]);

  // Limpiar notificaciones antiguas (más de 7 días)
  const clearOldNotifications = useCallback(async () => {
    if (!token) return;

    try {
      await fetch(`${API_URL}/api/notifications/clear-old`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      await fetchNotifications();
    } catch (error) {
      console.error('Error clearing old notifications:', error);
    }
  }, [token, fetchNotifications]);

  // Solicitar permiso de push notifications
  const requestPushPermission = useCallback(async () => {
    if (!isPushSupported()) {
      toast.error('Tu navegador no soporta notificaciones push');
      return false;
    }

    const permission = await requestNotificationPermission();
    setPushPermission(permission);

    if (permission === 'granted' && token) {
      const subscription = await subscribeToPush(token);
      if (subscription) {
        setPushSubscribed(true);
        toast.success('¡Notificaciones activadas!');
        return true;
      }
    } else if (permission === 'denied') {
      toast.error('Permisos de notificación denegados');
    }

    return false;
  }, [token]);

  // Verificar estado de push al cargar
  useEffect(() => {
    const checkPushStatus = async () => {
      if (isPushSupported()) {
        setPushPermission(getNotificationPermission());
        const subscribed = await isSubscribedToPush();
        setPushSubscribed(subscribed);
      }
    };
    checkPushStatus();
  }, []);

  // Suscribir automáticamente si tiene permiso pero no está suscrito
  useEffect(() => {
    const autoSubscribe = async () => {
      if (token && pushPermission === 'granted' && !pushSubscribed) {
        const subscription = await subscribeToPush(token);
        if (subscription) {
          setPushSubscribed(true);
        }
      }
    };
    autoSubscribe();
  }, [token, pushPermission, pushSubscribed]);

  // Cargar notificaciones al iniciar
  useEffect(() => {
    if (token) {
      fetchNotifications();
    } else {
      setNotifications([]);
      setLoading(false);
    }
  }, [token, fetchNotifications]);

  // Conectar WebSocket
  useEffect(() => {
    if (!user?._id && !user?.id) return;

    const userId = user._id || user.id;
    const newSocket = io(API_URL, {
      transports: ['websocket', 'polling'],
      auth: { token }
    });

    newSocket.on('connect', () => {
      console.log('Socket conectado para notificaciones');
      newSocket.emit('join', userId);
    });

    newSocket.on('new-notification', (notification) => {
      console.log('Nueva notificación recibida:', notification);

      // Agregar a la lista
      setNotifications(prev => [notification, ...prev]);

      // Mostrar toast en la app
      const toastType = notification.type === 'order' ? 'success' : 'default';
      if (toastType === 'success') {
        toast.success(notification.title || notification.message, {
          duration: 4000,
        });
      } else {
        toast(notification.title || notification.message, {
          duration: 4000,
          icon: '🔔',
        });
      }

      // Mostrar notificación push si la app está en segundo plano
      if (document.visibilityState === 'hidden' && pushPermission === 'granted') {
        showLocalNotification(notification.title || 'Devolada', {
          body: notification.message,
          tag: notification.type || 'default',
          data: notification
        });
      }
    });

    newSocket.on('disconnect', () => {
      console.log('Socket desconectado');
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [user, token, pushPermission]);

  // Escuchar mensajes del Service Worker (clicks en notificaciones)
  useEffect(() => {
    const handleSWMessage = (event) => {
      if (event.data?.type === 'NOTIFICATION_CLICK') {
        const { data, url } = event.data;

        // Marcar como leída si tiene ID
        if (data?._id) {
          markAsRead(data._id);
        }

        // Navegar si hay URL (el SW ya maneja la navegación,
        // pero podemos hacer acciones adicionales aquí)
        console.log('Notificación clickeada:', data, url);
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleSWMessage);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
    };
  }, [markAsRead]);

  // Agrupar notificaciones por fecha
  const groupedNotifications = notifications.reduce((groups, notification) => {
    const date = new Date(notification.createdAt);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    let groupKey;
    if (date.toDateString() === today.toDateString()) {
      groupKey = 'Hoy';
    } else if (date.toDateString() === yesterday.toDateString()) {
      groupKey = 'Ayer';
    } else {
      groupKey = date.toLocaleDateString('es-MX', {
        weekday: 'long',
        day: 'numeric',
        month: 'short'
      });
    }

    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(notification);

    return groups;
  }, {});

  const pushSupported = useMemo(() => isPushSupported(), []);

  const value = useMemo(() => ({
    notifications,
    groupedNotifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearOldNotifications,
    pushSupported,
    pushPermission,
    pushSubscribed,
    requestPushPermission,
  }), [notifications, groupedNotifications, unreadCount, loading, fetchNotifications, markAsRead, markAllAsRead, deleteNotification, clearOldNotifications, pushSupported, pushPermission, pushSubscribed, requestPushPermission]);

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export default NotificationsContext;
